import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon, M, Modal, TextArea } from 'design-system-web';
import styles from './RequestModal.scss';
import { attachmentProps, Attachment } from '../AttachmentModal/AttachmentModal';
import { DropdownSelection } from '../DropDown/DropDown';
import { useTranslation } from 'react-i18next';
import { FileReturnType, FileReturnTypeEnum, validateFiles } from '../../utils/fileReaderResolver';
import { LegacyButton } from '../Button/Button';
import { ValidatedTextArea } from '../TextArea/validateTextArea';
import { FileInput } from '../FileInput/FileInput';
import { useVantifyCategories, useVantifySites, useVantifySubCategories, useVantifyUser, useWorkOrder, VantifyOperation } from 'integrations-vantify-react-hooks';
import { DropdownOption } from '../../utils/WorkOrder';
import { Alert, AlertType } from '../Alert/Alert';

type AttachmentWithFile = Omit<attachmentProps, 'onDelete'> & {
  file?: File;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (jobId: number) => void;
};

export function RequestModal({ isOpen, onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<AttachmentWithFile[]>();
  const [summary, setSummary] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [subCategoryId, setSubCategoryId] = useState<string>('');
  const [serviceProviderId, setServiceProviderId] = useState<string>('');
  const [approvedById, setApprovedById] = useState<string>('');
  const [errorFiles, setErrorFiles] = useState<Array<{ fileName: string; errorMessage: string }>>([]);
  const { categories, fetchCategories } = useVantifyCategories();
  const { subCategories, fetchSubCategories } = useVantifySubCategories();
  const { serviceProviders, fetchServiceProviders } = useVantifyUser();
  const { siteAssociates, fetchSiteAssociates } = useVantifySites();
  const {
    createWorkOrder,
    siteId,
    reporter,
    siteContact,
    error,
    loading,
    uploadFile,
    addWorkOrderAttachment,
    userId,
    shouldAutoApprove,
  } = useWorkOrder();

  const isSubmitting =
  loading[VantifyOperation.CREATE_WORK_ORDER] ||
  loading[VantifyOperation.UPLOAD_FILE] ||
  loading[VantifyOperation.ADD_WORK_ORDER_ATTACHMENT] ||
  loading[VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL] ||
  loading[VantifyOperation.ACCEPT_WORK_ORDER_ASSIGNMENT] ||
  false;

  const categoryOptions = useMemo<DropdownOption[]>(
    () => (categories || []).map(category => ({ id: String(category.id), name: category.name })),
    [categories]
  );

  const subCategoryOptions = useMemo<DropdownOption[]>(
    () => (subCategories || []).map(subCategory => ({ id: String(subCategory.id), name: subCategory.name })),
    [subCategories]
  );


  const serviceProviderOptions = useMemo<DropdownOption[]>(
    () => {
      if (!shouldAutoApprove) return [];

      return (serviceProviders || []).map(serviceProvider => ({ 
        id: String(serviceProvider.id), 
        name: serviceProvider.name 
      }));
    },
    [serviceProviders, shouldAutoApprove]
  );

  const approverOptions = useMemo<DropdownOption[]>(
    () => {
      if (!shouldAutoApprove) return [];

      return (siteAssociates || []).map(siteAssociate => ({ 
        id: String(siteAssociate.id), 
        name: siteAssociate.title 
      }));
    },
    [siteAssociates, shouldAutoApprove]
  );

  const deleteFile = useCallback(async (fileId?: string | number) => {
    setAttachments(prev => (prev ?? []).filter(att => att.id !== fileId));
  }, []);

  const handleFilesSelected = useCallback((files: FileReturnType[]) => {
    const fileObjects = files.filter((file): file is File => file instanceof File);

    if (fileObjects.length === 0) {
      return;
    }

    const failedFiles: Array<{ fileName: string; errorMessage: string }> = [];
    const validFiles: File[] = [];

    fileObjects.forEach((file) => {
      const validation = validateFiles(file);

      if (!validation.isValid && validation.error) {
        failedFiles.push({ fileName: file.name, errorMessage: validation.error });
      } else {
        validFiles.push(file);
      }
    });

    if (failedFiles.length > 0) {
      setErrorFiles(prev => [...prev, ...failedFiles]);
    }

    if (validFiles.length > 0) {
      setAttachments(prev => {
        const currentLength = prev?.length ?? 0;
        const newAttachments: AttachmentWithFile[] = validFiles.map((file, index) => {
          return {
            id: `temp-${Date.now()}-${currentLength + index}`,
            display_name: file.name,
            file,
          } as AttachmentWithFile;
        });

        return [...(prev || []), ...newAttachments];
      });
    }
  }, []);

  const resetForm = useCallback(() => {
    setSummary('');
    setDescription('');
    setCategoryId('');
    setSubCategoryId('');
    setServiceProviderId('');
    setApprovedById('');
    setAttachments([]);
    setErrorFiles([]);
  }, []);

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const isFormValid = useMemo(() => {
    return Boolean(
      summary.trim() && 
      categoryId && 
      description.trim() && 
      siteId &&
      errorFiles.length === 0
    );
  }, [summary, categoryId, description, siteId, errorFiles.length]);

  const handleSubmit = useCallback(async () => {
    if (!summary.trim() || !categoryId || !description.trim() || !siteId) return;

      const fileUploadPromises = (attachments || [])
        .filter(att => att.file)
        .map(async (attachment) => {
          if (!attachment.file) return null;
          
          const uploadResponse = await uploadFile({
            file: attachment.file,
            filename: attachment.display_name,
            contentType: attachment.file.type,
          });

          const fileMetadata = uploadResponse?.data?.data;
          
          // Extract file number from policy key (e.g., "132.jpeg" -> "132")
          const policyKey = fileMetadata?.policy?.key;

          if (!policyKey) {
            return null;
          }
          
          const fileNumber = policyKey.split('.')[0];
          const fileHref = `/files/${fileNumber}`;

          return {
            href: fileHref,
            title: attachment.display_name,
          };
        });

      const uploadedFiles = (await Promise.all(fileUploadPromises)).filter(Boolean) as Array<{ href: string; title: string }>;

      const jobGroupId = subCategoryId ? Number(subCategoryId) : Number(categoryId);
      const response = await createWorkOrder({
        siteId,
        jobGroupId,
        reporter,
        siteContact,
        summary: summary.trim(),
        description: description.trim(),
        approverUserId: approvedById ? Number(approvedById) : undefined,
        serviceProviderId: serviceProviderId ? Number(serviceProviderId) : undefined,
      });

      if (!response?.data?.id) {
        return;
      }

      const jobId = response.data.id;

      if (uploadedFiles.length > 0 && siteId) {
        await Promise.all(
          uploadedFiles.map(file =>
            addWorkOrderAttachment({
              siteId,
              jobId,
              fileHref: file.href,
              title: file.title,
            })
          )
        );
      }

      resetForm();
      onClose();

      if (onSuccess) {
        onSuccess(jobId);
      }
    
  }, [summary, categoryId, description, subCategoryId, siteId, reporter, siteContact, createWorkOrder, resetForm, onClose, onSuccess, attachments, uploadFile, addWorkOrderAttachment, approvedById, serviceProviderId]);

  useEffect(() => {
    fetchCategories();
  },[]);

  useEffect(() => {
  if (categoryId) {
    fetchSubCategories({ jobGroupId: Number(categoryId) });
  }

  setSubCategoryId('');
}, [categoryId]);

  useEffect(() => {
    if (!userId || !siteId || !shouldAutoApprove) {
      setServiceProviderId('');
      setApprovedById('');

      return;
    }

    fetchServiceProviders(Number(userId));
    fetchSiteAssociates(Number(siteId));
  }, [userId, siteId, shouldAutoApprove]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('New Request')}
      size="large"
      className={styles.modalOverride}
      actions={
        <div className={styles.submitCancelButton}>
          <LegacyButton
            fullWidth
            variant="contained"
            className={styles.cancelButton}
            onClick={handleCancel}
          >
            {t('shared.integrations.vantify.workorder.cancel')}
          </LegacyButton>
          <LegacyButton 
            fullWidth 
            variant="contained" 
            className={styles.button}
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? t('shared.integrations.vantify.workorder.submitLoading')
              : t('shared.integrations.vantify.workorder.submit')}
          </LegacyButton>
        </div>
      }
    >
      <div className={styles.requestFieldsLabel}>
        {t('shared.integrations.vantify.workorder.requestfields')} {' '}
        <span className={styles.requiredAsterisk}>*</span>
      </div>
      {error[VantifyOperation.CREATE_WORK_ORDER] && (
        <Alert type={AlertType.error} fullWidth>
          {error[VantifyOperation.CREATE_WORK_ORDER]}
        </Alert>
      )}
      <div className={styles.form}>
        <div className={styles.formSection}>
          <div className={styles.buildingSelectionDropdownContainer}>
            <div className={styles.label}>
              {t('shared.integrations.vantify.workorder.summary')} <span className={styles.requiredAsterisk}>*</span>
            </div>
            <TextArea
              testId="summary"
              placeholder={t(
                'shared.integrations.vantify.workorder.summary.placeholder'
              )}
              value={summary}
              onChange={(value: string) => {
                setSummary(value);
              }}
              maxLength={50}
              showLengthIndicator
            />
          </div>
          <div className={styles.buildingSelectionDropdownContainer}>
            <div className={styles.label}>
              {t('shared.integrations.vantify.description')} <span className={styles.requiredAsterisk}>*</span>
            </div>
            <ValidatedTextArea
              placeholder={t(
                'shared.integrations.vantify.workorder.description.placeholder'
              )}
              validationClassName={styles.textareaValidation}
              onChange={(value: string) => setDescription(value)}
              minRows={5}
              value={description}
              validation={null}
              className={styles.textArea}
              maxLength={400}
              showLengthIndicator
            />
            </div>
          </div>

          <div className={styles.buildingSelectionDropdownContainer}>
            <DropdownSelection
              DropdownOptions={categoryOptions}
              DropdownValue={categoryId}
              setDropdownValue={setCategoryId}
              showLabel
              label={t('shared.integrations.vantify.category')}
              showAsterisk
              placeholder='shared.integrations.vantify.workorder.category.placeholder'
            />
          </div>

          <div className={styles.buildingSelectionDropdownContainer}>
            <DropdownSelection
              DropdownOptions={subCategoryOptions}
              DropdownValue={subCategoryId}
              setDropdownValue={setSubCategoryId}
              showLabel
              label={t('shared.integrations.vantify.subcategory')}
              placeholder='shared.integrations.vantify.workorder.subcategory.placeholder'
              disabled={!categoryId}
            />
          </div>

        {shouldAutoApprove && (
          <>
            <div className={styles.buildingSelectionDropdownContainer}>
              <DropdownSelection
                DropdownOptions={serviceProviderOptions}
                DropdownValue={serviceProviderId}
                setDropdownValue={setServiceProviderId}
                showLabel
                label={t('shared.integrations.vantify.serviceprovider')}
                placeholder="shared.integrations.vantify.serviceprovider.placeholder"
                disabled={!serviceProviderOptions.length}
              />
            </div>

            <div className={styles.buildingSelectionDropdownContainer}>
              <DropdownSelection
                DropdownOptions={approverOptions}
                DropdownValue={approvedById}
                setDropdownValue={setApprovedById}
                showLabel
                label={t('shared.integrations.vantify.approvedby')}
                placeholder="shared.integrations.vantify.approvedby.placeholder"
                disabled={!approverOptions.length}
              />
            </div>
          </>
        )}
          
        <div className={styles.formSection}>
          <div className={styles.buildingSelectionDropdownContainer}>
          <div className={styles.attachmentsHeader}>
            <M bold>{t('shared.integrations.vantify.attachments')}</M>
            <M className={styles.attachmentNotice}>
              {t('shared.integrations.vantify.workorder.attachments.notice')}
            </M>
          </div>

          <FileInput
            accept="*/*"
            type={FileReturnTypeEnum.File}
            enableMultiUpload
            onFilesSelected={handleFilesSelected}
          >
            <LegacyButton className={styles.buttonAttachments}>
              <div className={styles.row}>
                <Icon
                  className={styles.emptyImageIcon}
                  set="FontAwesome"
                  name="paperclip"
                  type="fal"
                />
                <M className={styles.attachFileText}>
                  {t('shared.integrations.vantify.attachfile')}
                </M>
              </div>
            </LegacyButton>
          </FileInput>
          {errorFiles.map((errorFile, index) => (
            <Attachment
              key={`${errorFile.fileName}-${index}`}
              display_name={errorFile.fileName}
              errorMessage={errorFile.errorMessage}
              onDelete={async (_fileId?: string | number) => {
                setErrorFiles(prev => prev.filter(ef => ef.fileName !== errorFile.fileName));
              }}
              error
            />
          ))}
          {attachments?.map((attachment, index) => {
            return (
              <Attachment
                key={attachment.id ?? attachment.file_association_id ?? `attachment-${index}`}
                id={attachment.id}
                file_association_id={attachment.file_association_id}
                display_name={attachment.display_name}
                thumbnail={attachment.thumbnail}
                created_at={attachment.created_at}
                onDelete={(fileId) =>
                  deleteFile(fileId)
                }
                testId="close-icon"
              />
            );
          })}  
        </div>
        </div>
      </div>
    </Modal>
  );
}